import React, { useEffect, useState } from 'react';
import { useInstallPrompt } from '../hooks/useInstallPrompt.js';
import { trackEvent, hasGeneratedBefore } from '../firebase/config.js';
import { Events } from '../firebase/events.js';

const DISMISS_KEY = 'ff:installBannerDismissed';

const InstallAppBanner = () => {
    const { canInstall, isInstalled, isIos, promptInstall } = useInstallPrompt();
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; }
    });
    const [shownTracked, setShownTracked] = useState(false);
    // Gated on having actually generated something, not on payment tier — a free-trial user
    // who's already gotten value is a better install candidate than someone mid-checkout,
    // and installing during the trial (home-screen habit, more return visits) is exactly
    // the kind of thing that nudges free -> paid, so restricting this to already-paying
    // subscribers would cut it out of the phase where it likely helps most. This component
    // stays mounted across in-app route changes, so listen for the same-tab signal
    // trackEvent() fires the moment a first generation succeeds, rather than only picking it
    // up on the next full page load.
    const [hasGenerated, setHasGenerated] = useState(hasGeneratedBefore);
    useEffect(() => {
        if (hasGenerated) return;
        const onFirstGeneration = () => setHasGenerated(true);
        window.addEventListener('ff:firstGeneration', onFirstGeneration);
        return () => window.removeEventListener('ff:firstGeneration', onFirstGeneration);
    }, [hasGenerated]);

    // iOS never fires beforeinstallprompt, so it gets static Share-menu instructions
    // instead of a one-tap install button.
    const showChromeBanner = canInstall;
    const showIosBanner = isIos && !isInstalled && !canInstall;
    const shouldShow = hasGenerated && !dismissed && (showChromeBanner || showIosBanner);

    useEffect(() => {
        if (shouldShow && !shownTracked) {
            try { trackEvent(showChromeBanner ? Events.pwa_install_banner_shown : Events.pwa_ios_banner_shown); } catch (_) {}
            setShownTracked(true);
        }
    }, [shouldShow, shownTracked, showChromeBanner]);

    if (!shouldShow) return null;

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
        try { trackEvent(showChromeBanner ? Events.pwa_install_banner_dismissed : Events.pwa_ios_banner_dismissed); } catch (_) {}
        setDismissed(true);
    };

    const handleInstallClick = async () => {
        try { trackEvent(Events.pwa_install_clicked); } catch (_) {}
        const choice = await promptInstall();
        if (choice) {
            try { trackEvent(choice.outcome === 'accepted' ? Events.pwa_install_accepted : Events.pwa_install_declined); } catch (_) {}
            // Don't persist a dismissal here — `isInstalled` already hides the banner while
            // the app is actually installed. A permanent flag would (and did) wrongly keep
            // the banner hidden forever if the user later uninstalls and could use it again.
        }
    };

    return (
        <div className="sticky top-0 z-[60] w-full bg-teal-500/15 border-b border-teal-500/30 backdrop-blur px-4 py-2 flex items-center justify-center gap-3 text-xs sm:text-sm font-medium text-teal-100">
            {showChromeBanner ? (
                <>
                    <span>Install FlashFonic for quick access and offline studying.</span>
                    <button
                        type="button"
                        onClick={handleInstallClick}
                        className="shrink-0 rounded-full px-3 py-1 text-[11px] sm:text-xs font-semibold text-slate-900 bg-teal-300 hover:bg-teal-200 transition"
                    >
                        Install
                    </button>
                </>
            ) : (
                <span>Add FlashFonic to your Home Screen: tap <strong>Share</strong>, then <strong>&ldquo;Add to Home Screen&rdquo;</strong> for quick access and offline studying.</span>
            )}
            <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss"
                className="shrink-0 text-teal-200/70 hover:text-white transition"
            >
                &#10005;
            </button>
        </div>
    );
};

export default InstallAppBanner;
